import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  ActivityKindEnum,
  AdFieldOccupancyEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type {
  PropertyAdAmenityField,
  PropertyAdEditorFormValues,
} from '../../types/propertyAdEditor';

import { PROPERTY_AD_AMENITY_FIELDS } from './propertyAdAmenities';
import {
  normalizeAmenityTransition,
  normalizeActivityTransition,
  normalizeCompletionTransition,
  normalizeOccupancyTransition,
  normalizePropertyKindTransition,
  normalizeTransactionTransition,
} from './propertyAdEditorNormalization';

const dirtyChangeOptions = { shouldDirty: true } as const;

export const usePropertyAdEditorNormalization = () => {
  const { getValues, setValue } = useFormContext<PropertyAdEditorFormValues>();

  const applyNormalizedValues = useCallback(
    (currentValues: PropertyAdEditorFormValues, normalizedValues: PropertyAdEditorFormValues) => {
      if (currentValues.activityKind !== normalizedValues.activityKind) {
        setValue('activityKind', normalizedValues.activityKind, dirtyChangeOptions);
      }
      if (currentValues.transactionType !== normalizedValues.transactionType) {
        setValue('transactionType', normalizedValues.transactionType, dirtyChangeOptions);
      }
      if (currentValues.defaultRentFrequency !== normalizedValues.defaultRentFrequency) {
        setValue('defaultRentFrequency', normalizedValues.defaultRentFrequency, dirtyChangeOptions);
      }
      if (currentValues.propertyKind !== normalizedValues.propertyKind) {
        setValue('propertyKind', normalizedValues.propertyKind, dirtyChangeOptions);
      }
      if (currentValues.price !== normalizedValues.price) {
        setValue('price', normalizedValues.price, dirtyChangeOptions);
      }
      if (currentValues.sellingPrice !== normalizedValues.sellingPrice) {
        setValue('sellingPrice', normalizedValues.sellingPrice, dirtyChangeOptions);
      }
      if (currentValues.completionStatus !== normalizedValues.completionStatus) {
        setValue('completionStatus', normalizedValues.completionStatus, dirtyChangeOptions);
      }
      if (currentValues.toSeller !== normalizedValues.toSeller) {
        setValue('toSeller', normalizedValues.toSeller, dirtyChangeOptions);
      }
      if (currentValues.toDeveloper !== normalizedValues.toDeveloper) {
        setValue('toDeveloper', normalizedValues.toDeveloper, dirtyChangeOptions);
      }
      if (currentValues.postHandover !== normalizedValues.postHandover) {
        setValue('postHandover', normalizedValues.postHandover, dirtyChangeOptions);
      }
      if (currentValues.handover !== normalizedValues.handover) {
        setValue('handover', normalizedValues.handover, dirtyChangeOptions);
      }
      if (currentValues.priceYearly !== normalizedValues.priceYearly) {
        setValue('priceYearly', normalizedValues.priceYearly, dirtyChangeOptions);
      }
      if (currentValues.priceMonthly !== normalizedValues.priceMonthly) {
        setValue('priceMonthly', normalizedValues.priceMonthly, dirtyChangeOptions);
      }
      if (currentValues.priceWeekly !== normalizedValues.priceWeekly) {
        setValue('priceWeekly', normalizedValues.priceWeekly, dirtyChangeOptions);
      }
      if (currentValues.priceDaily !== normalizedValues.priceDaily) {
        setValue('priceDaily', normalizedValues.priceDaily, dirtyChangeOptions);
      }
      if (currentValues.currency !== normalizedValues.currency) {
        setValue('currency', normalizedValues.currency, dirtyChangeOptions);
      }
      if (currentValues.currencyCommission !== normalizedValues.currencyCommission) {
        setValue('currencyCommission', normalizedValues.currencyCommission, dirtyChangeOptions);
      }
      if (currentValues.builtUpArea !== normalizedValues.builtUpArea) {
        setValue('builtUpArea', normalizedValues.builtUpArea, dirtyChangeOptions);
      }
      if (currentValues.beds !== normalizedValues.beds) {
        setValue('beds', normalizedValues.beds, dirtyChangeOptions);
      }
      if (currentValues.baths !== normalizedValues.baths) {
        setValue('baths', normalizedValues.baths, dirtyChangeOptions);
      }
      if (currentValues.floor !== normalizedValues.floor) {
        setValue('floor', normalizedValues.floor, dirtyChangeOptions);
      }
      if (currentValues.furniture !== normalizedValues.furniture) {
        setValue('furniture', normalizedValues.furniture, dirtyChangeOptions);
      }
      if (currentValues.renovation !== normalizedValues.renovation) {
        setValue('renovation', normalizedValues.renovation, dirtyChangeOptions);
      }
      if (currentValues.occupancy !== normalizedValues.occupancy) {
        setValue('occupancy', normalizedValues.occupancy, dirtyChangeOptions);
      }
      if (currentValues.rentedTill !== normalizedValues.rentedTill) {
        setValue('rentedTill', normalizedValues.rentedTill, dirtyChangeOptions);
      }
      if (
        currentValues.view.length !== normalizedValues.view.length ||
        currentValues.view.some((value, index) => value !== normalizedValues.view[index])
      ) {
        setValue('view', [...normalizedValues.view], dirtyChangeOptions);
      }
      PROPERTY_AD_AMENITY_FIELDS.forEach((field) => {
        if (currentValues[field] !== normalizedValues[field]) {
          setValue(field, normalizedValues[field], dirtyChangeOptions);
        }
      });
    },
    [setValue]
  );

  const changeActivity = useCallback(
    (nextActivity: ActivityKindEnum | null) => {
      const currentValues = getValues();
      const normalizedValues = normalizeActivityTransition(
        currentValues.activityKind,
        nextActivity,
        currentValues
      );

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  const changeTransactionType = useCallback(
    (nextType: TransactionTypeEnum | null) => {
      const currentValues = getValues();
      const normalizedValues = normalizeTransactionTransition(
        currentValues.transactionType,
        nextType,
        currentValues
      );

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  const changeCompletionStatus = useCallback(
    (nextStatus: CompletionStatusEnum | null) => {
      const currentValues = getValues();
      const normalizedValues = normalizeCompletionTransition(
        currentValues.completionStatus,
        nextStatus,
        currentValues
      );

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  const changeOccupancy = useCallback(
    (nextOccupancy: AdFieldOccupancyEnum | null) => {
      const currentValues = getValues();
      const normalizedValues = normalizeOccupancyTransition(
        currentValues.occupancy,
        nextOccupancy,
        currentValues
      );

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  const changePropertyKind = useCallback(
    (nextKind: PropertyKindEnum | null) => {
      const currentValues = getValues();
      const normalizedValues = normalizePropertyKindTransition(
        currentValues.propertyKind,
        nextKind,
        currentValues
      );

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  const changeAmenity = useCallback(
    (field: PropertyAdAmenityField, nextValue: boolean) => {
      const currentValues = getValues();
      const normalizedValues = normalizeAmenityTransition(field, nextValue, currentValues);

      applyNormalizedValues(currentValues, normalizedValues);
    },
    [applyNormalizedValues, getValues]
  );

  return {
    changeActivity,
    changeAmenity,
    changeCompletionStatus,
    changeOccupancy,
    changePropertyKind,
    changeTransactionType,
  };
};
